import { useState, type ChangeEvent, type FormEvent } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRight, Mail, MailCheck } from 'lucide-react'
import { Alert, Button } from '@agion/ui'
import { AuthSplitLayout } from '@/components/layout/AuthSplitLayout'
import { Field } from '@/components/common/Field'
import { authService } from '@/services/auth'
import { passwordResetErrorMessage } from '@/services/auth-errors'
import { hasEmailShape } from '@/utils/validation'

/**
 * Ask for a password reset link (agion/Agion#2375). auth-service answers 202 for
 * every well-formed address, so after a send the page shows one confirmation
 * that is the same whether or not the address has a password account.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSent, setIsSent] = useState(false)

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    setEmailError('')
    setError('')
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    // The form is noValidate; a name typed into the email field is caught here.
    const address = email.trim()
    if (!hasEmailShape(address)) {
      setEmailError(t('authErrors.invalidEmail'))
      return
    }

    setIsSubmitting(true)
    try {
      await authService.forgotPassword({ email: address })
      setIsSent(true)
    } catch (err) {
      setError(passwordResetErrorMessage(err, t, 'request'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const backToSignIn = (
    <Link to="/login" className="lp-back">
      <ArrowLeft size={16} aria-hidden="true" />
      {t('passwordReset.backToSignIn')}
    </Link>
  )

  if (isSent) {
    return (
      <AuthSplitLayout>
        <div className="lp-state lp-pop" role="status">
          <div className="lp-badge lp-badge-success">
            <MailCheck size={24} aria-hidden="true" />
          </div>
          <h2 className="lp-state-title">{t('passwordReset.sentTitle')}</h2>
          <p className="lp-state-msg">{t('passwordReset.sentMessage')}</p>
          <hr className="lp-state-divider" />
          {backToSignIn}
        </div>
      </AuthSplitLayout>
    )
  }

  return (
    <AuthSplitLayout>
      <div className="lp-heading-block lp-rise lp-rise-2">
        <h2 className="lp-heading">{t('passwordReset.title')}</h2>
        <p className="lp-subheading">{t('passwordReset.description')}</p>
      </div>

      <form className="lp-form lp-rise lp-rise-3" onSubmit={handleSubmit} noValidate>
        {error && <Alert variant="error">{error}</Alert>}

        <Field
          id="email"
          type="email"
          label={t('passwordReset.emailLabel')}
          value={email}
          onChange={handleEmailChange}
          error={emailError}
          required
          autoComplete="email"
          placeholder={t('waitlist.emailPlaceholder')}
          leadingIcon={<Mail size={16} />}
          disabled={isSubmitting}
        />
        <Button
          type="submit"
          className="lp-btn-primary w-full"
          disabled={isSubmitting || !email.trim()}
          isLoading={isSubmitting}
        >
          {isSubmitting ? t('passwordReset.sending') : t('passwordReset.submit')}
          {!isSubmitting && <ArrowRight size={16} aria-hidden="true" />}
        </Button>
      </form>

      <p className="lp-secondary-cta">{backToSignIn}</p>
    </AuthSplitLayout>
  )
}

export default ForgotPasswordPage
